import { CHARACTER_SCHEMA_VERSION, EMPTY_CHARACTER, type Character, type PackContentRef, type Skill } from '../types/character'
import { useContentStore } from '@/entities/content'

import { namespaceClassChoices } from './choices'

/**
 * Key shapes from before the race → species rename that can still arrive from
 * old data: localStorage saves, Supabase rows, shared links, and downloaded
 * character files all carry raw `Character` JSON written by older builds.
 */
interface LegacyCharacterKeys {
  raceId?: string
  subraceId?: string
  /** `loot` was briefly called `customItems`. */
  customItems?: Character['loot']
}

interface LegacyMetaKeys {
  raceName?: string
}

/** Pre-multiclass shape: a single class and level, folded into `classes[]` by `foldClassIntoArray`. */
interface LegacyClassKeys {
  classId?: string
  level?: number
}

/**
 * `classChoices` keys the app itself generates as bare literals under the
 * single-class model (see `levelChoiceKey`/`suffix` in `lib/choices.ts`) — as
 * opposed to a content-authored choice id (e.g. `species_choice`), which is
 * already globally unique and left alone.
 */
const NAMESPACED_KEYS = ['subclass', 'expertise', 'eldritch-invocations', 'fightingStyle', 'skill', 'cantrip', 'spell', 'asiOrFeat', 'feat'] as const

/**
 * Bring character JSON of any age to the current shape: each migration after the
 * character's `schemaVersion` runs once, in order, so a save from any build catches
 * up in one pass however many releases it skipped. Only shape changes belong here;
 * decisions the rules now ask for are the character page's catch-up (`levelUpPlan`
 * with `catchUp`). Run this on every boundary where character JSON enters the app.
 */
export function migrateCharacterKeys(raw: Character): Character {
  let c = raw
  for (const migration of MIGRATIONS.slice(raw.schemaVersion ?? 0)) c = migration(c)
  // A save from before multiclassing holds its class's picks under bare choice ids (Weapon
  // Mastery, a feat's spells). Migration 2 could only rename the keys the app itself names;
  // which of the rest are the class's is in the content, so they move here, with what is
  // loaded. A pack class loaded later is caught by `upgradeStoredIds` on the character page.
  // (An old file may lack fields the lookup reads; defaults stand in for them, and only the keys are kept.)
  if ((raw.schemaVersion ?? 0) < 2 && c.classes && c.classChoices)
    c = { ...c, classChoices: namespaceClassChoices({ ...EMPTY_CHARACTER, ...c }, useContentStore.getState()).classChoices }
  return c.schemaVersion === CHARACTER_SCHEMA_VERSION ? c : { ...c, schemaVersion: CHARACTER_SCHEMA_VERSION }
}

/**
 * Migration 1. Map a character's pre-rename keys onto the current schema, idempotently:
 * `raceId` → `speciesId`, `subraceId` → `subspeciesId`, `customItems` → `loot`,
 * and `contentRefs` entries of type `races` → `species`. Current-schema input
 * passes through unchanged.
 */
function renameLegacyKeys(raw: Character): Character {
  const c = { ...raw } as Character & LegacyCharacterKeys
  if (c.raceId !== undefined) {
    if (!c.speciesId) c.speciesId = c.raceId
    delete c.raceId
  }
  if (c.subraceId !== undefined) {
    if (!c.subspeciesId) c.subspeciesId = c.subraceId
    delete c.subraceId
  }
  if (c.customItems !== undefined) {
    if (!c.loot?.length) c.loot = c.customItems
    delete c.customItems
  }
  if (c.contentRefs?.some((p) => p.entries.some((e) => (e.type as string) === 'races'))) {
    c.contentRefs = c.contentRefs.map(
      (pack): PackContentRef => ({
        ...pack,
        entries: pack.entries.map((e) =>
          (e.type as string) === 'races' ? { ...e, type: 'species' } : e,
        ),
      }),
    )
  }
  return c
}

/**
 * Migration 2. Folds the pre-multiclass `classId`/`level` into `classes: [{ classId,
 * level }]`, and renames the app-generated (not content-authored) `classChoices`
 * keys that were global literals under the single-class model — 'subclass' being
 * the important one, since two classes' subclass picks would otherwise collide —
 * to `${classId}#${key}`. Safe without content access: every pre-migration
 * character has exactly one class, so every key unambiguously belongs to it.
 */
function foldClassIntoArray(raw: Character): Character {
  const c = { ...raw } as Character & LegacyClassKeys
  if (c.classId === undefined) return c
  const classId = c.classId
  c.classes = [{ classId, level: c.level ?? 1 }]
  delete c.classId
  delete c.level
  const renamed: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(c.classChoices ?? {})) {
    renamed[(NAMESPACED_KEYS as readonly string[]).includes(key) ? `${classId}#${key}` : key] = value
  }
  c.classChoices = renamed
  return c
}

/**
 * Migration 3. Folds the pre-multiclass flat `skillProficiencies` array under the
 * character's one class — the same "exactly one class" invariant `foldClassIntoArray`
 * (migration 2, which always runs first) already relies on, so `classes[0]` is safe
 * to read here. A character already on the per-class shape (an object, not an array)
 * passes through unchanged.
 */
function foldSkillProficiencies(raw: Character): Character {
  if (!Array.isArray(raw.skillProficiencies)) return raw
  const flat = raw.skillProficiencies as unknown as Skill[]
  const classId = raw.classes[0]?.classId ?? ''
  return { ...raw, skillProficiencies: { [classId]: flat } }
}

/**
 * Migrations by the `schemaVersion` they produce (index N → version N+1). Append only:
 * a released migration never changes, and `CHARACTER_SCHEMA_VERSION` is their count.
 */
const MIGRATIONS: ((c: Character) => Character)[] = [renameLegacyKeys, foldClassIntoArray, foldSkillProficiencies]

/**
 * Same for saved-character meta: `raceName` → `speciesName`. Typed loosely so
 * the store's meta type doesn't have to be imported here (avoids a lib → model
 * dependency).
 */
export function migrateMetaKeys<M extends { speciesName?: string }>(raw: M | undefined): M | undefined {
  if (!raw) return raw
  const m = { ...raw } as M & LegacyMetaKeys
  if (m.raceName !== undefined) {
    if (!m.speciesName) m.speciesName = m.raceName
    delete m.raceName
  }
  return m
}
